import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Alert,
  Container,
  Fade,
  Grid,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import {
  Code,
  CloudQueue,
  Storage,
  Security,
} from '@mui/icons-material';
import WorkOSLoginButton from './WorkOSLoginButton';

interface LoginFormProps {}

const LoginForm: React.FC<LoginFormProps> = () => {
  const [error, setError] = useState('');
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const nextError = searchParams.get('error');
    setError(nextError ? decodeURIComponent(nextError) : '');
  }, [location.search]);

  const handleSSOError = (errorMsg: string) => {
    setError(errorMsg);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#0a0e27',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 4, md: 0 }, // Add vertical padding on mobile
        // --- (All background/animation styles remain the same) ---
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 20%, rgba(124, 58, 237, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 70%)
          `,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(rgba(139, 92, 246, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(139, 92, 246, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          animation: 'gridMove 20s linear infinite',
        },
        '@keyframes gridMove': {
          '0%': { transform: 'translate(0, 0)' },
          '100%': { transform: 'translate(50px, 50px)' },
        },
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
          '50%': { transform: 'translateY(-20px) rotate(5deg)' },
        },
        '@keyframes pulse': {
          '0%, 100%': { opacity: 0.3 },
          '50%': { opacity: 0.6 },
        },
        '@keyframes glow': {
          '0%, 100%': { boxShadow: '0 0 20px rgba(139, 92, 246, 0.3)' },
          '50%': { boxShadow: '0 0 40px rgba(139, 92, 246, 0.6)' },
        },
      }}
    >
      {/* --- (Animated Shapes & Icons remain the same, they are decorative) --- */}
      <Box
        sx={{
          position: 'absolute',
          top: '15%',
          left: '10%',
          width: { xs: 50, md: 100 }, // Smaller on mobile
          height: { xs: 50, md: 100 },
          background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.1), rgba(59, 130, 246, 0.1))',
          borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
          animation: 'float 8s ease-in-out infinite',
          filter: 'blur(40px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '60%',
          right: '15%',
          width: { xs: 100, md: 150 }, // Smaller on mobile
          height: { xs: 100, md: 150 },
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(124, 58, 237, 0.1))',
          borderRadius: '70% 30% 30% 70% / 70% 70% 30% 30%',
          animation: 'float 10s ease-in-out infinite reverse',
          filter: 'blur(50px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          left: '20%',
          width: { xs: 40, md: 80 }, // Smaller on mobile
          height: { xs: 40, md: 80 },
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1))',
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite, pulse 3s ease-in-out infinite',
          filter: 'blur(30px)',
          pointerEvents: 'none',
        }}
      />
      <Code
        sx={{
          position: 'absolute',
          top: '12%',
          right: '25%',
          fontSize: { xs: 30, md: 50 },
          color: 'rgba(139, 92, 246, 0.15)',
          animation: 'float 7s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />
      <CloudQueue
        sx={{
          position: 'absolute',
          bottom: '15%',
          right: '20%',
          fontSize: { xs: 25, md: 45 },
          color: 'rgba(59, 130, 246, 0.15)',
          animation: 'float 9s ease-in-out infinite reverse',
          pointerEvents: 'none',
        }}
      />
      <Storage
        sx={{
          position: 'absolute',
          top: '70%',
          left: '15%',
          fontSize: { xs: 20, md: 40 },
          color: 'rgba(124, 58, 237, 0.15)',
          animation: 'float 8s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />


      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* --- RESPONSIVE REFACTOR --- */}
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          {/* Left Side - Branding */}
          <Grid item xs={12} md={6}>
            <Fade in timeout={600}>
              {/* --- RESPONSIVE REFACTOR --- */}
              <Box sx={{ 
                pr: { md: 4 }, 
                textAlign: { xs: 'center', md: 'left' } // Center text on mobile
              }}>
                {/* Logo with enhanced styling */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: { xs: 3, md: 5 }, // Responsive margin
                    position: 'relative',
                  }}
                >
                  <Box
                    component="img"
                    src="/logo-dark.png"
                    alt="Ottonomy Logo"
                    sx={{
                      // --- RESPONSIVE REFACTOR ---
                      height: { xs: 100, md: 140 }, // Responsive logo size
                      width: 'auto',
                      filter: 'brightness(0) invert(1) drop-shadow(0 0 30px rgba(124, 58, 237, 0.5))',
                      animation: 'logoFloat 4s ease-in-out infinite',
                      '@keyframes logoFloat': {
                        '0%, 100%': { 
                          transform: 'translateY(0px)',
                          filter: 'brightness(0) invert(1) drop-shadow(0 0 30px rgba(124, 58, 237, 0.5))',
                        },
                        '50%': { 
                          transform: 'translateY(-10px)',
                          filter: 'brightness(0) invert(1) drop-shadow(0 0 40px rgba(124, 58, 237, 0.8))',
                        },
                      },
                    }}
                  />
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #a78bfa 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 3,
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    // --- RESPONSIVE REFACTOR ---
                    fontSize: { xs: '2.25rem', md: '3rem' } // Responsive font size
                  }}
                >
                  Deployment Tracking Platform
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    mb: { xs: 4, md: 5 }, // Responsive margin
                    lineHeight: 1.6,
                    maxWidth: { xs: '100%', md: 500 }, // Allow full width on mobile
                    mx: { xs: 'auto', md: 0 }, // Center on mobile
                    fontWeight: 300,
                    // --- RESPONSIVE REFACTOR ---
                    fontSize: { xs: '1rem', md: '1.25rem' } // Responsive font size
                  }}
                >
                  Monitor and track your software deployments with version history
                </Typography>
              </Box>
            </Fade>
          </Grid>

          {/* Right Side - Login Card */}
          <Grid item xs={12} md={6}>
            <Fade in timeout={800}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
                  backdropFilter: 'blur(30px)',
                  borderRadius: { xs: 3, md: 4 }, // Slightly smaller radius on mobile
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  boxShadow: '0 30px 90px rgba(124, 58, 237, 0.3), 0 0 100px rgba(124, 58, 237, 0.1)',
                  // --- RESPONSIVE REFACTOR ---
                  p: { xs: 3, sm: 4, md: 6 }, // Responsive padding
                  position: 'relative',
                  zIndex: 2,
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '5px',
                    background: 'linear-gradient(90deg, #7c3aed, #3b82f6, #06b6d4, #3b82f6, #7c3aed)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 4s linear infinite',
                  },
                  '@keyframes shimmer': {
                    '0%': { backgroundPosition: '200% 0' },
                    '100%': { backgroundPosition: '-200% 0' },
                  },
                }}
              >
                {/* Welcome Section with Icon */}
                <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      // --- RESPONSIVE REFACTOR ---
                      width: { xs: 60, md: 80 }, // Responsive icon box
                      height: { xs: 60, md: 80 },
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
                      boxShadow: '0 10px 40px rgba(124, 58, 237, 0.4)',
                      mb: 2, // Responsive margin
                      animation: 'pulse 3s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { 
                          transform: 'scale(1)',
                          boxShadow: '0 10px 40px rgba(124, 58, 237, 0.4)',
                        },
                        '50%': { 
                          transform: 'scale(1.05)',
                          boxShadow: '0 15px 50px rgba(124, 58, 237, 0.6)',
                        },
                      },
                    }}
                  >
                    {/* --- RESPONSIVE REFACTOR --- */}
                    <Security sx={{ fontSize: { xs: 30, md: 40 }, color: 'white' }} />
                  </Box>

                  <Typography
                    variant="h3"
                    fontWeight="800"
                    sx={{
                      mb: 1, // Responsive margin
                      background: 'linear-gradient(135deg, #ffffff 0%, #a78bfa 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      // --- RESPONSIVE REFACTOR ---
                      fontSize: { xs: '2rem', md: '2.5rem' } // Responsive font size
                    }}
                  >
                    Welcome
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.6)',
                      mb: { xs: 3, md: 4 }, // Responsive margin
                    }}
                  >
                    Sign in to access your dashboard
                  </Typography>
                </Box>

                {error && (
                  <Fade in>
                    <Alert
                      severity="error"
                      sx={{
                        mb: 3,
                        borderRadius: '16px',
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        color: '#fca5a5',
                        '& .MuiAlert-icon': {
                          color: '#fca5a5',
                        },
                      }}
                    >
                      {error}
                    </Alert>
                  </Fade>
                )}

                {/* SSO Login Button */}
                <Box sx={{ mb: { xs: 3, md: 4 } }}>
                  <WorkOSLoginButton onError={handleSSOError} fullWidth size="large" />
                </Box>

                {/* Security Badge */}
                <Box
                  sx={{
                    pt: { xs: 3, md: 4 }, // Responsive padding
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#10b981',
                        animation: 'blink 2s ease-in-out infinite',
                        '@keyframes blink': {
                          '0%, 100%': { opacity: 1 },
                          '50%': { opacity: 0.3 },
                        },
                      }}
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontWeight: 500,
                      }}
                    >
                      Secured with enterprise SSO
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Fade>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LoginForm;